/**
 * Lot Home Features Card HTML Template
 *
 * Returns HTML string for the home features card shown in the Lot & Elevation panel.
 *
 * @module nativeInteractiveSiteMap/components/lotHomeFeature.dom
 */

import { checkCircleIconSvg, backButtonIconSvg } from 'constants/svg-assets';

/**
 * Normalizes a feature object/string into a display label.
 * @param {Object|string} feature
 * @returns {string}
 */
function getFeatureLabel(feature) {
  if (typeof feature === 'string') {
    return feature.trim();
  }

  if (!feature || typeof feature !== 'object') {
    return '';
  }

  return String(feature.description || '').trim();
}

/**
 * Generates a single feature item row.
 * @param {string} featureLabel
 * @returns {string}
 */
function getFeatureItemHtml(featureLabel) {
  return `
    <li class="nhs-c-lot-home-features-card__item nhs-u-display--flex nhs-u-items-center nhs-u-gap--200">
      <span class="nhs-c-lot-home-features-card__icon" aria-hidden="true">
        ${checkCircleIconSvg}
      </span>
      <span class="nhs-o-typography--sans-body-1-small-regular nhs-u-color--black-1000">${featureLabel}</span>
    </li>
  `;
}

/**
 * Generates the Home Features card HTML.
 * @param {Array} highlights - List of home features from selected spec
 * @returns {string} HTML string for the home features card, or empty string if no features
 */
export default function getLotHomeFeaturesCardHtml(highlights = []) {
  const featureLabels = (Array.isArray(highlights) ? highlights : [])
    .map(getFeatureLabel)
    .filter(Boolean);

  if (!featureLabels.length) {
    return '';
  }

  const featuresHtml = featureLabels.map(getFeatureItemHtml).join('');

  return `
    <div class="nhs-c-lot-home-features-card">
      <details class="nhs-c-lot-home-features-card__disclosure" open>
        <summary class="nhs-c-lot-home-features-card__summary nhs-u-display--flex nhs-u-justify-content-space-between nhs-u-items-center nhs-u-cursor--pointer">
          <h2 class="nhs-c-lot-home-features-card__title nhs-o-typography--sans-body-1-medium-regular nhs-u-color--black-1000">Home Features</h2>
          <span class="nhs-c-lot-home-features-card__chevron" aria-hidden="true">
            ${backButtonIconSvg}
          </span>
        </summary>
        <ul class="nhs-c-lot-home-features-card__list">
          ${featuresHtml}
        </ul>
      </details>
    </div>
  `;
}
